import React, { useState, useEffect, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import SystemService from "../../../services/SystemService";
import queryString from "query-string";
import { getErrorMessage } from "../../../helpers/functions";
import { useSnackbar } from "notistack";
import Footer from "../NewLandingPage/components/Footer";
import { Header } from "../../internal/Header/Header";

function SkeletonItem() {
  return (
    <SkeletonCard>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "1.2rem",
        }}
      >
        <SkeletonLine $h="10px" $w="60px" $mb="0" />
        <SkeletonLine
          $h="20px"
          $w="72px"
          $mb="0"
          style={{ borderRadius: "20px" }}
        />
      </div>
      <SkeletonLine $h="16px" $w="85%" />
      <SkeletonLine $h="16px" $w="60%" $mb="1.2rem" />
      <SkeletonLine $h="11px" $w="90%" />
      <SkeletonLine $h="11px" $w="70%" $mb="1.4rem" />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <SkeletonLine $h="10px" $w="50px" $mb="0" />
        <SkeletonLine
          $h="32px"
          $w="90px"
          $mb="0"
          style={{ borderRadius: "8px" }}
        />
      </div>
    </SkeletonCard>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function getWebinarTitle(item) {
  return (
    item?.hero?.titleLine2 ||
    item?.hero?.titleLine1 ||
    item?.slug?.replace(/-/g, " ")?.replace(/\b\w/g, (c) => c.toUpperCase()) ||
    `Webinar #${item.id}`
  );
}

function getDateLabel(item) {
  return item?.hero?.meta?.find((m) => m.icon === "calendar")?.label || null;
}

function getTimeLabel(item) {
  return item?.hero?.meta?.find((m) => m.icon === "clock")?.label || null;
}

function buildPagesArray(currentPage, totalPages) {
  if (totalPages <= 7)
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  const pages = [];
  if (currentPage <= 4) {
    pages.push(1, 2, 3, 4, 5, "…", totalPages);
  } else if (currentPage >= totalPages - 3) {
    pages.push(
      1,
      "…",
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    );
  } else {
    pages.push(
      1,
      "…",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "…",
      totalPages,
    );
  }
  return pages;
}

const IconCalendar = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const IconClock = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const IconHash = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="4" y1="9" x2="20" y2="9" />
    <line x1="4" y1="15" x2="20" y2="15" />
    <line x1="10" y1="3" x2="8" y2="21" />
    <line x1="16" y1="3" x2="14" y2="21" />
  </svg>
);

const IconArrowRight = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const IconChevronLeft = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const IconChevronRight = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const IconSearch = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const IconEmpty = () => (
  <svg
    width="36"
    height="36"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14.5 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

async function fetchWebinarConfigs(query) {
  const response = await SystemService.fetchWebinarConfigs(query);
  return { ...response };
}

const PER_PAGE = 10;

const PastWebinars = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const load = useCallback(
    async (pageNum = 1, searchVal = "") => {
      setLoading(true);
      try {
        const params = { page: pageNum, perPage: PER_PAGE };
        if (searchVal?.trim()) params.query = searchVal.trim();
        const qs = `?${queryString.stringify(params)}`;
        const result = await fetchWebinarConfigs(qs);
        const pastWebinars = Array.isArray(result?.data)
          ? result.data.filter((item) => item?.status !== "upcoming")
          : [];
        setWebinars(pastWebinars);
        setPage(result.page || pageNum);
        setLastPage(result.lastPage || 1);
      } catch (error) {
        console.error("Failed to load webinars:", error);
        enqueueSnackbar(getErrorMessage(error), { variant: "error" });
        setWebinars([]);
        setLastPage(1);
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  // Initial load - runs only once
  useEffect(() => {
    load(1, "");
  }, [load]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      if (searchInput !== search) {
        load(1, searchInput);
      }
    }, 380);

    return () => clearTimeout(timer);
  }, [searchInput, load, search]);

  const goToPage = (p) => {
    if (p < 1 || p > lastPage || p === page) return;
    load(p, search);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pages = buildPagesArray(page, lastPage);

  return (
    <>
      <Header>
        <PageBg>
          <PageContainer>
            <PageHead>
              <Eyebrow>Webinar Archive</Eyebrow>
              <PageTitle>Past Webinars</PageTitle>
              <PageSubtitle>
                Browse all previously held and upcoming webinars managed through
                IAMC.
              </PageSubtitle>
            </PageHead>

            <Toolbar>
              <SearchWrap>
                <SearchIcon>
                  <IconSearch />
                </SearchIcon>
                <SearchInput
                  placeholder="Search webinars…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </SearchWrap>
              {!loading && (
                <ResultCount>
                  <strong>{webinars.length}</strong> webinar
                  {webinars.length !== 1 ? "s" : ""} found
                </ResultCount>
              )}
            </Toolbar>

            {loading ? (
              <Grid>
                {Array.from({ length: PER_PAGE }).map((_, i) => (
                  <SkeletonItem key={i} />
                ))}
              </Grid>
            ) : webinars.length === 0 ? (
              <EmptyWrap>
                <EmptyIllustration>
                  <IconEmpty />
                </EmptyIllustration>
                <EmptyTitle>No webinars found</EmptyTitle>
                <EmptyText>
                  {search
                    ? `No results for "${search}". Try a different search.`
                    : "No webinars have been created yet."}
                </EmptyText>
              </EmptyWrap>
            ) : (
              <>
                <Grid>
                  {webinars.map((item, idx) => {
                    const title = getWebinarTitle(item);
                    const date = getDateLabel(item);
                    const time = getTimeLabel(item);
                    const status = item.status || "past";

                    return (
                      <Card
                        key={item.id}
                        $index={idx}
                        $status={status}
                        onClick={() =>
                          window.open(`/past-webinars/${item.id}`, "_blank")
                        }
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) =>
                          e.key === "Enter" &&
                          window.open(`/past-webinars/${item.id}`, "_blank")
                        }
                        aria-label={`View webinar: ${title}`}
                      >
                        {/* ... rest of your card JSX remains same ... */}
                        <CardTop>
                          <WebinarIdBadge>
                            <IconHash /> &nbsp;WEB-
                            {String(item.id).padStart(4, "0")}
                          </WebinarIdBadge>
                          <StatusPill $status={status}>
                            {status === "live" && <LiveDot />}
                            {status}
                          </StatusPill>
                        </CardTop>

                        <CardBody>
                          <WebinarTitle>{title}</WebinarTitle>
                          <SlugText title={item.slug}>/{item.slug}</SlugText>

                          <MetaRow>
                            {date && (
                              <MetaItem>
                                <IconCalendar />
                                <strong>{date}</strong>
                                {item?.hero?.meta?.find(
                                  (m) => m.icon === "calendar",
                                )?.sublabel && (
                                  <span>
                                    ·{" "}
                                    {
                                      item.hero.meta.find(
                                        (m) => m.icon === "calendar",
                                      ).sublabel
                                    }
                                  </span>
                                )}
                              </MetaItem>
                            )}
                            {time && (
                              <MetaItem>
                                <IconClock />
                                {time} IST
                              </MetaItem>
                            )}
                            <MetaItem>
                              <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ color: "#00838c" }}
                              >
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                              </svg>
                              Created{" "}
                              <strong>{formatDate(item.created_at)}</strong>
                            </MetaItem>
                            {item.updated_at &&
                              item.updated_at !== item.created_at && (
                                <MetaItem>
                                  <svg
                                    width="13"
                                    height="13"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    style={{ color: "#00838c" }}
                                  >
                                    <polyline points="23 4 23 10 17 10" />
                                    <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
                                  </svg>
                                  Updated{" "}
                                  <strong>{formatDate(item.updated_at)}</strong>
                                </MetaItem>
                              )}
                          </MetaRow>

                          <Divider />
                          <CardFooter>
                            <CardId>#{item.id}</CardId>
                            <ViewBtn
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(
                                  `/past-webinars/${item.id}`,
                                  "_blank",
                                );
                              }}
                            >
                              View Details <IconArrowRight />
                            </ViewBtn>
                          </CardFooter>
                        </CardBody>
                      </Card>
                    );
                  })}
                </Grid>

                {lastPage > 1 && (
                  <PaginationWrap>
                    <PageBtn
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 1}
                    >
                      <IconChevronLeft />
                    </PageBtn>

                    {pages.map((p, i) =>
                      p === "…" ? (
                        <PageEllipsis key={`ellipsis-${i}`}>…</PageEllipsis>
                      ) : (
                        <PageBtn
                          key={p}
                          $active={p === page}
                          onClick={() => goToPage(p)}
                          aria-label={`Page ${p}`}
                          aria-current={p === page ? "page" : undefined}
                        >
                          {p}
                        </PageBtn>
                      ),
                    )}

                    <PageBtn
                      onClick={() => goToPage(page + 1)}
                      disabled={page === lastPage}
                    >
                      <IconChevronRight />
                    </PageBtn>
                  </PaginationWrap>
                )}
              </>
            )}
          </PageContainer>
        </PageBg>
      </Header>
      <Footer />
    </>
  );
};

export default PastWebinars;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.5; }
`;

const PageBg = styled.div`
  min-height: 100vh;
  background: #f0f3fc;
  padding-bottom: 3rem;
`;

const PageContainer = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 2.5rem clamp(1rem, 4vw, 2.5rem) 1rem;
`;

const PageHead = styled.div`
  margin-bottom: 2.2rem;
  animation: ${fadeUp} 0.55s ease both;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.68rem;
  font-family: openSans-SemiBold, sans-serif;
  color: #00838c;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  margin-bottom: 0.6rem;

  &::before {
    content: "";
    width: 22px;
    height: 2px;
    background: #00838c;
    border-radius: 1px;
  }
`;

const PageTitle = styled.h1`
  font-family: openSans-SemiBold, sans-serif;
  font-size: clamp(1.6rem, 3.5vw, 2.4rem);
  font-weight: 800;
  color: #293461;
  letter-spacing: -0.5px;
  margin-bottom: 0.4rem;
`;

const PageSubtitle = styled.p`
  font-family: openSans-Regular, sans-serif;
  font-size: 0.9rem;
  color: #949ab0;
  line-height: 1.6;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.6rem;
  flex-wrap: wrap;
  animation: ${fadeUp} 0.55s 0.08s ease both;
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  max-width: 340px;

  @media (max-width: 600px) {
    max-width: 100%;
    width: 100%;
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 0.9rem;
  top: 50%;
  transform: translateY(-50%);
  color: #949ab0;
  display: flex;
  align-items: center;
  pointer-events: none;

  svg {
    width: 15px;
    height: 15px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.65rem 1rem 0.65rem 2.4rem;
  border-radius: 10px;
  border: 1.5px solid rgba(41, 52, 97, 0.1);
  background: #ffffff;
  font-family: openSans-Regular, sans-serif;
  font-size: 0.85rem;
  color: #293461;
  outline: none;
  transition:
    border-color 0.22s,
    box-shadow 0.22s;

  &::placeholder {
    color: #bbc0d4;
  }

  &:focus {
    border-color: #00838c;
    box-shadow: 0 0 0 3px rgba(0, 131, 140, 0.1);
  }
`;

const ResultCount = styled.div`
  font-family: openSans-Regular, sans-serif;
  font-size: 0.8rem;
  color: #949ab0;
  white-space: nowrap;

  strong {
    font-family: openSans-SemiBold, sans-serif;
    color: #293461;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.3rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  background: #ffffff;
  border-radius: 18px;
  border: 1px solid rgba(41, 52, 97, 0.07);
  box-shadow: 0 2px 12px rgba(41, 52, 97, 0.06);
  overflow: hidden;
  cursor: pointer;
  transition:
    transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.3s ease;
  animation: ${fadeUp} 0.5s ease both;
  animation-delay: ${({ $index }) => Math.min($index * 0.06, 0.36)}s;
  position: relative;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 16px 48px rgba(41, 52, 97, 0.13);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${({ $status }) =>
      $status === "upcoming"
        ? "linear-gradient(90deg, #00838c, #00b4bf)"
        : $status === "live"
          ? "linear-gradient(90deg, #e53e3e, #fc8181)"
          : "linear-gradient(90deg, #d4a853, #f0c97a)"};
  }
`;

const CardTop = styled.div`
  padding: 1.4rem 1.4rem 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
`;

const WebinarIdBadge = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 0.65rem;
  font-weight: 700;
  color: #949ab0;
  letter-spacing: 1.5px;
  text-transform: uppercase;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.28rem 0.75rem;
  border-radius: 20px;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  white-space: nowrap;

  ${({ $status }) =>
    $status === "upcoming"
      ? `background: rgba(0,131,140,0.1); color: #00838c;`
      : $status === "live"
        ? `background: rgba(229,62,62,0.1); color: #e53e3e;`
        : `background: rgba(212,168,83,0.13); color: #b8892e;`}
`;

const LiveDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #e53e3e;
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

const CardBody = styled.div`
  padding: 1rem 1.4rem 1.4rem;
`;

const WebinarTitle = styled.h3`
  font-family: openSans-SemiBold, sans-serif;
  font-size: clamp(0.92rem, 1.8vw, 1.05rem);
  font-weight: 700;
  color: #293461;
  margin-bottom: 0.5rem;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SlugText = styled.p`
  font-family: openSans-Regular, sans-serif;
  font-size: 0.75rem;
  color: #bbc0d4;
  margin-bottom: 1.1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MetaRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  margin-bottom: 1.3rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: openSans-Regular, sans-serif;
  font-size: 0.78rem;
  color: #949ab0;

  svg {
    flex-shrink: 0;
    color: #00838c;
  }
  strong {
    font-family: openSans-SemiBold, sans-serif;
    color: #293461;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(41, 52, 97, 0.06);
  margin-bottom: 1.1rem;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ViewBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1.1rem;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #293461, #3d4a7a);
  color: #fff;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.22s ease;
  letter-spacing: 0.3px;

  svg {
    transition: transform 0.2s;
  }

  &:hover {
    background: linear-gradient(135deg, #1a2247, #293461);
    box-shadow: 0 4px 14px rgba(41, 52, 97, 0.28);

    svg {
      transform: translateX(3px);
    }
  }
`;

const CardId = styled.span`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 0.68rem;
  color: #bbc0d4;
`;

const SkeletonBase = styled.div`
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    rgba(41, 52, 97, 0.06) 25%,
    rgba(41, 52, 97, 0.11) 50%,
    rgba(41, 52, 97, 0.06) 75%
  );
  background-size: 600px 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;

const SkeletonCard = styled.div`
  background: #ffffff;
  border-radius: 18px;
  border: 1px solid rgba(41, 52, 97, 0.07);
  overflow: hidden;
  padding: 1.4rem;

  &::before {
    content: "";
    display: block;
    height: 3px;
    background: rgba(41, 52, 97, 0.06);
    margin: -1.4rem -1.4rem 1.4rem;
  }
`;

const EmptyWrap = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  animation: ${fadeUp} 0.5s ease;
`;

const EmptyIllustration = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(0, 131, 140, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.4rem;

  svg {
    color: #00838c;
  }
`;

const EmptyTitle = styled.h3`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 1.1rem;
  font-weight: 700;
  color: #293461;
  margin-bottom: 0.4rem;
`;

const EmptyText = styled.p`
  font-family: openSans-Regular, sans-serif;
  font-size: 0.86rem;
  color: #949ab0;
`;

const PaginationWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  margin-top: 2.4rem;
  flex-wrap: wrap;
  animation: ${fadeUp} 0.45s 0.3s ease both;
`;

const PageBtn = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 9px;
  border: 1.5px solid
    ${({ $active }) => ($active ? "#00838c" : "rgba(41,52,97,0.12)")};
  background: ${({ $active }) => ($active ? "#00838c" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#ffffff" : "#293461")};
  font-family: openSans-SemiBold, sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.38 : 1)};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:not(:disabled):hover {
    border-color: #00838c;
    color: ${({ $active }) => ($active ? "#ffffff" : "#00838c")};
    background: ${({ $active }) =>
      $active ? "#006d75" : "rgba(0,131,140,0.06)"};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const PageEllipsis = styled.span`
  width: 38px;
  text-align: center;
  color: #949ab0;
  font-size: 0.82rem;
`;

const SkeletonLine = styled(SkeletonBase)`
  height: ${({ $h }) => $h || "14px"};
  width: ${({ $w }) => $w || "100%"};
  margin-bottom: ${({ $mb }) => $mb || "0.6rem"};
`;
